import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { refreshToken, isTokenExpired } from '../services/authService';
import { API_CONFIG } from '../config/env';
import { clearAuthData, getAccessToken, normalizeToken, persistAuthTokens } from './auth';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  // Request interceptor to add auth token
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Ensure headers exist so we can safely assign keys below
      if (!config.headers) {
        config.headers = {} as any;
      }

      const token = getAccessToken();
      if (token && !isTokenExpired(token)) {
        (config.headers as any).Authorization = `Bearer ${token}`;
      }

      const corApiKey = API_CONFIG.COR_API_KEY;
      if (corApiKey) {
        // `apiKey` is the parameter name used by some backend handlers.
        (config.headers as any)['X-COR-ApiKey'] = corApiKey;
        (config.headers as any)['apiKey'] = corApiKey;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue the request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            if (originalRequest.headers) {
              (originalRequest.headers as any).Authorization = `Bearer ${token}`;
            }
            return axios(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshTokenValue = normalizeToken(localStorage.getItem('refreshToken'));
        const username = localStorage.getItem('username');

        if (!refreshTokenValue || !username) {
          clearAuthData();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        try {
          const response = await refreshToken({ token: refreshTokenValue, name: username });
          if (!persistAuthTokens(response)) {
            clearAuthData();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          const access = getAccessToken();
          if (originalRequest.headers && access) {
            (originalRequest.headers as any).Authorization = `Bearer ${access}`;
          }

          processQueue(null, access);

          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearAuthData();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};
